import { Button, Group, Box, useMantineTheme, Text, Flex } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import ImageUpload from "src/components/ImageUpload";
import {
  CreatePolicyInput,
  UpdatePolicyInput,
  useCreatePolicyMutation,
  useGetEditorLinkLazyQuery,
} from "src/generated/graphql";
import { memo, useEffect, useRef, useState } from "react";
import TextInput from "src/components/TextInput/TextInput";
import { MIME_TYPES } from "@mantine/dropzone";
import { errorNotification } from "src/utils/alerts";
import { object, string } from "yup";
import EditorModal from "../PolicyEditor";
import { closeModal } from "@mantine/modals";
import { ModalId } from "src/utils/constants";

type PolicyFormProps = {
  handleClose: () => void;
  onRefetch: () => void;
};

type ErrorProps = {
  isDraft: boolean;
  values: UpdatePolicyInput;
};

export const checkPolicyError = ({ isDraft, values }: ErrorProps) => {
  let error = "";
  if (!isDraft && !values.fileB64) {
    error = "PDF file is required.";
  }
  return error;
};

export const policyFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
});

const PolicyForm = ({ handleClose, onRefetch }: PolicyFormProps) => {
  const theme = useMantineTheme();
  const [isDraft, setisDraft] = useState(false);

  const [getEditorLink, { data: editorLink, loading: EditorLoading }] =
    useGetEditorLinkLazyQuery();

  const [createLoc, { loading }] = useCreatePolicyMutation({
    onCompleted: (data) => {
      if (data.createPolicy.documentId) {
        getEditorLink({
          variables: {
            id: data.createPolicy.documentId,
          },
        });
      }
      onRefetch();
    },
  });

  const form = useForm({
    initialValues: {
      name: "",
      fileB64: "",
    },

    validate: yupResolver(policyFormSchema),
  });

  const createPolicy = async (values: CreatePolicyInput) => {
    if (isDraft) {
      delete values.fileB64;
    }
    let error = checkPolicyError({ isDraft, values });
    if (error)
      return errorNotification({
        message: error,
      });

    await createLoc({
      variables: {
        data: values,
      },
    });
  };

  useEffect(() => {
    // Close CreatePolicy Modal
    window.addEventListener("message", function (event) {
      if (event.data === "closeModal") {
        closeModal(ModalId.CreatePolicy);
      }
    });
  }, []);

  return (
    <>
      {editorLink?.getEditorLink ? (
        <Box w={"90vw"} h={"70vh"}>
          <EditorModal link={editorLink.getEditorLink} />
        </Box>
      ) : (
        <Box sx={{ minWidth: 360 }} mx="auto">
          <form
            onSubmit={form.onSubmit((values) => {
              createPolicy(values);
            })}
          >
            <TextInput
              withAsterisk
              label="Name"
              {...form.getInputProps("name")}
            />

            {loading || EditorLoading ? (
              <Text my={20} color="blue">
                Please hang on tight for a few seconds. <br />
                Your document is being created.
              </Text>
            ) : (
              <>
                <Group position="apart" mt="lg" mb="lg" pl={"lg"} pr={"lg"}>
                  <Button
                    radius="xl"
                    compact
                    variant="outline"
                    onClick={() => setisDraft(false)}
                    style={{
                      flex: 1,
                      borderColor: !isDraft ? theme.colors.dark[8] : "#DDD",
                      borderWidth: !isDraft ? 2 : 1,
                      backgroundColor: theme.colors.primary[4],
                    }}
                  >
                    Upload PDF
                  </Button>

                  <Text>Or</Text>
                  <Button
                    radius="xl"
                    compact
                    onClick={() => setisDraft(true)}
                    variant="outline"
                    style={{
                      flex: 1,
                      borderColor: isDraft ? theme.colors.dark[8] : "#DDD",
                      borderWidth: isDraft ? 2 : 1,
                      backgroundColor: theme.colors.primary[4],
                    }}
                  >
                    Draft Policy
                  </Button>
                </Group>

                {!isDraft ? (
                  <div
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                      maxWidth: 250,
                      margin: "auto",
                    }}
                  >
                    <ImageUpload
                      accept={[MIME_TYPES.pdf]}
                      onReadSuccess={(b64) =>
                        form.setFieldValue("fileB64", b64)
                      }
                      src={form.values.fileB64}
                      alt="Policy Pdf"
                      isPdf={true}
                    />
                  </div>
                ) : null}
              </>
            )}

            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={handleClose}
                style={{
                  flex: 1,
                }}
                styles={{
                  root: {
                    backgroundColor: theme.colors.secondary[6],
                  },
                  inner: {
                    color: theme.colors.secondaryTextColor[3],
                  },
                  label: {
                    color: theme.colors.secondaryTextColor[3],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={
                  loading || EditorLoading || !form.getInputProps("name").value
                }
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={loading || EditorLoading}
              >
                {isDraft ? "Begin Drafting" : "Upload Document"}
              </Button>
            </Group>
          </form>
        </Box>
      )}
    </>
  );
};

export default memo(PolicyForm);
